/**
 * ShiftNursePicker — browse-first, shift-scoped picker for the incoming nurse.
 *
 * Replaces the blind name-typeahead in the handover dialog. The backend enforces
 * "the incoming nurse must be on an open shift," so instead of typing a name and
 * hitting a submit-time 409, the nurse SEES exactly the colleagues who satisfy
 * that constraint — the ward's on-shift nurses, rendered as tappable cards. The
 * constraint becomes visible and browsable rather than a hidden rule.
 *
 * A secondary, understated search affordance falls back to the same `searchUsers`
 * typeahead — it covers float nurses who are on shift but not on this ward's
 * list. The browse list is primary; search is the escape hatch.
 *
 * Presentational: the parent owns the ward-allocation fetch and passes
 * `onShiftNurses` (already ordered, already excluding self/outgoing). The only
 * fetch this component makes is the search fallback.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { searchUsers, type UserSearchResult } from '@/api/endpoints/users';
import { Skeleton } from '@/components/ui/skeleton';
import {
  UserSearchCombobox,
  userDisplayName,
  type SearchDataSource,
} from '@/components/user/UserSearchCombobox';
import { cn } from '@/lib/utils';

/** The search fallback queries the live directory (debounce, sequence guard and
 *  truncation hint all live in the combobox). Module-level so its identity is
 *  stable across renders. */
const DIRECTORY_SEARCH: SearchDataSource = { type: 'server', searchFn: searchUsers };

/** A nurse chosen from either the on-shift browse list or the search
 *  fallback — reported as `{ id, name }` so the caller can both submit the
 *  id and show the name. Owned here (not a standalone NurseSelect
 *  component — deleted as dead code, nothing rendered it) since this is the
 *  only picker HandoverDialog actually uses. */
export interface SelectedNurse {
  id: string;
  name: string;
}

export interface OnShiftNurse {
  id: string;
  name: string;
  role?: string;
  specialisation?: string;
  patientCount: number;
}

interface ShiftNursePickerProps {
  /** Colleagues on shift on this ward — already ordered, already excluding self/outgoing. */
  onShiftNurses: OnShiftNurse[];
  value: SelectedNurse | null;
  onChange: (nurse: SelectedNurse | null) => void;
  /** Ward allocation still loading. */
  isLoading: boolean;
  /** Ward allocation failed. */
  isError: boolean;
  onRetry: () => void;
  /** Ids to hide from the SEARCH fallback too (outgoing nurse/self). */
  excludeIds?: string[];
  inputId: string;
}

function initialFor(name: string): string {
  const trimmed = name.trim();
  return (trimmed[0] ?? '?').toUpperCase();
}

export function ShiftNursePicker({
  onShiftNurses,
  value,
  onChange,
  isLoading,
  isError,
  onRetry,
  excludeIds,
  inputId,
}: ShiftNursePickerProps) {
  const { t } = useTranslation();

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const isEmpty = !isLoading && !isError && onShiftNurses.length === 0;

  // A value picked from search (a float nurse not on this ward's list) keeps
  // the search revealed and reflected, so the choice stays visible.
  useEffect(() => {
    if (value != null && !onShiftNurses.some((n) => n.id === value.id)) {
      setQuery(value.name);
      setSearchOpen(true);
    }
  }, [value, onShiftNurses]);

  const handleSearchChange = useCallback(
    (input: string) => {
      setQuery(input);
      setSearchOpen(true); // pin open once the nurse starts typing
      onChange(null);
    },
    [onChange],
  );

  // Hide self/outgoing AND the nurses already browsable as cards, so search
  // only surfaces genuinely "other" colleagues.
  const searchExcludeIds = [...(excludeIds ?? []), ...onShiftNurses.map((n) => n.id)];

  const handleSelectCard = useCallback(
    (nurse: OnShiftNurse) => {
      if (value?.id === nurse.id) {
        onChange(null); // tapping the selected card deselects
        return;
      }
      onChange({ id: nurse.id, name: nurse.name });
      // Picking from browse collapses the search escape hatch.
      setSearchOpen(false);
      setQuery('');
    },
    [value, onChange],
  );

  const handleSelectSuggestion = useCallback(
    (user: UserSearchResult) => {
      const name = userDisplayName(user);
      onChange({ id: user.id, name });
      setQuery(name);
    },
    [onChange],
  );

  // Search is shown on demand, or auto-shown where there is no browsable list
  // (error / empty) — a float nurse may still be on shift elsewhere.
  const showSearch = searchOpen || isError || isEmpty;

  return (
    <div className="space-y-3">
      <p
        id={`${inputId}-label`}
        className="font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400"
      >
        {t('shifts.handover.picker.onShift', { defaultValue: 'On shift' })}
      </p>

      {isLoading ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-[14px] border border-neutral-200 px-4 py-3"
            >
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-12 rounded-full" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-[14px] border border-neutral-200 bg-neutral-50 px-4 py-4">
          <p className="text-[14px] leading-relaxed text-neutral-600">
            {t('shifts.handover.picker.loadError', {
              defaultValue: 'Could not load who is on shift.',
            })}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-sm text-[14px] font-semibold text-primary-500 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 motion-safe:transition-transform motion-safe:active:scale-[0.97]"
          >
            {t('actions.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      ) : isEmpty ? (
        <div className="rounded-[14px] border border-neutral-200 bg-neutral-50 px-4 py-4">
          <p className="text-[14px] leading-relaxed text-neutral-600">
            {t('shifts.handover.picker.empty', {
              defaultValue: 'No colleagues on shift on your ward.',
            })}
          </p>
        </div>
      ) : (
        <ul
          role="list"
          aria-labelledby={`${inputId}-label`}
          className="space-y-2"
        >
          {onShiftNurses.map((nurse) => {
            const selected = value?.id === nurse.id;
            const secondary = nurse.role ?? nurse.specialisation;
            return (
              <li key={nurse.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleSelectCard(nurse)}
                  className={cn(
                    'flex min-h-14 w-full items-center gap-3 rounded-[14px] border px-4 py-2.5 text-left',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                    'motion-safe:transition-transform motion-safe:active:scale-[0.97]',
                    selected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 bg-[var(--color-neutral-0)]',
                  )}
                >
                  <span
                    aria-hidden
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[15px] font-semibold text-primary-700"
                  >
                    {initialFor(nurse.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[16px] font-semibold tracking-[-0.2px] text-neutral-900">
                      {nurse.name || t('shifts.nurse.unknown', { defaultValue: 'Unknown' })}
                    </span>
                    {secondary && (
                      <span className="block truncate text-[13px] text-neutral-500">
                        {secondary}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'flex-shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold tabular-nums',
                      'bg-neutral-100 text-neutral-600',
                    )}
                  >
                    {nurse.patientCount === 0
                      ? t('shifts.nurse.free', { defaultValue: 'free' })
                      : t('shifts.nurse.patientLoad', {
                          count: nurse.patientCount,
                          defaultValue: '{{count}} Pat.',
                        })}
                  </span>
                  {selected && (
                    <Check
                      className="h-5 w-5 flex-shrink-0 text-primary-500"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Secondary escape hatch — search a nurse not on this ward's list. Kept
          visually quiet: a plain text link, not a second CTA. */}
      {showSearch ? (
        <div className="pt-1">
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-[13px] text-neutral-500"
          >
            {t('shifts.handover.picker.searchOther', {
              defaultValue: 'Search another nurse',
            })}
          </label>
          <UserSearchCombobox
            id={inputId}
            value={query}
            onChange={handleSearchChange}
            onSelect={handleSelectSuggestion}
            dataSource={DIRECTORY_SEARCH}
            excludeIds={searchExcludeIds}
            selectedUserId={value?.id ?? null}
            placeholder={t('shifts.nurse.placeholder', {
              defaultValue: 'Type a name',
            })}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-sm text-[14px] text-primary-500 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 motion-safe:transition-transform motion-safe:active:scale-[0.97]"
        >
          <Search className="h-3.5 w-3.5" aria-hidden />
          {t('shifts.handover.picker.searchOther', {
            defaultValue: 'Search another nurse',
          })}
        </button>
      )}
    </div>
  );
}
